'use client';
import MuiCheckbox from '@mui/material/Checkbox';
import PropTypes from 'prop-types';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { utilityNegative } from '../../colors';
import CheckboxCheckedIcon from '../../icons/CheckboxChecked';
import CheckboxErrorIcon from '../../icons/CheckboxError';
import CheckboxIndeterminateIcon from '../../icons/CheckboxIndeterminate';
import CheckboxUncheckedIcon from '../../icons/CheckboxUnchecked';
import idsTokensDefault from '../../utils/tokens/idsTokens';
import withRef from '../../utils/withRef';
import FormControlLabel from '../FormControlLabel';

export const styles = ({ idsTokens = idsTokensDefault }) => ({
  asterisk: {
    color: utilityNegative,
    position: 'relative',
    fontWeight: idsTokens.typography.font.weight.bold,
    bottom: 2,
    right: 40,
  },
  root: {
    display: 'inline-flex',
    gap: idsTokens.checkbox.default.spacing.standard.gap,
    paddingRight: idsTokens.checkbox.default.spacing.standard.right,
    paddingBottom: idsTokens.checkbox.default.spacing.standard.bottom,
    paddingTop: idsTokens.checkbox.default.spacing.standard.top,
    paddingLeft: idsTokens.checkbox.default.spacing.standard.left,
    boxSizing: 'border-box',
    marginRight: 'unset',
    marginLeft: -2,
    width: 'fit-content',
    '& .MuiCheckbox-indeterminate svg': {
      '& rect:last-of-type': {
        fill: idsTokens.checkbox.default.color.control.icon.enabled,
      },
    },
    '& .Mui-checked svg': {
      '& path:last-of-type': {
        fill: idsTokens.checkbox.default.color.control.icon.enabled,
      },
    },
    // Default hover not checked & not indeterminate
    '&:hover:not(:active)': {
      '& .MuiCheckbox-root:not(.MuiCheckbox-indeterminate)': {
        '& svg': {
          fill: idsTokens.checkbox.default.color.control.border.hover,
          color: 'unset',
          '& rect': {
            fill: idsTokens.checkbox.default.color.control.background.unselected.hover,
          },
        },
      },
      // Default hover checked & indeterminate
      '& .Mui-checked svg, & .MuiCheckbox-indeterminate svg': {
        fill: idsTokens.checkbox.default.color.control.background.selected.hover,
        '& rect:last-of-type, &path:last-of-type': {
          fill: idsTokens.checkbox.default.color.control.icon.hover,
        },
      },
    },
    '&:active:hover': {
      // Not checked & not indeterminate
      '& .MuiCheckbox-root:not(.MuiCheckbox-indeterminate)': {
        '& svg': {
          fill: idsTokens.checkbox.default.color.control.border.pressed,
          color: 'unset',
          '& rect': {
            fill: idsTokens.checkbox.default.color.control.background.unselected.pressed,
          },
        },
      },
      // Default hover checked & indeterminate
      '& .Mui-checked svg, & .MuiCheckbox-indeterminate svg': {
        fill: idsTokens.checkbox.default.color.control.background.selected.pressed,
        '& rect:last-of-type, &path:last-of-type': {
          fill: idsTokens.checkbox.default.color.control.icon.pressed,
        },
      },
    },
    '&:has(.Mui-focusVisible)': {
      outlineOffset: -1,
      outlineStyle: 'auto',
      outline: `${idsTokens.functional.borderWidth.focus.default} solid ${idsTokens.functional.color.border.focus}`,
      borderRadius: idsTokens.checkbox.default.radius.focus,
    },
    '&.Mui-disabled': {
      pointerEvents: 'none',
      '& .MuiFormControlLabel-label': {
        color: idsTokens.checkbox.default.color.label.disabled,
      },
    },
    '& .MuiFormControlLabel-label': {
      paddingTop: 'unset',
      paddingBottom: 0,
      paddingLeft: 0,
      color: idsTokens.checkbox.default.color.label.enabled,
      lineHeight: idsTokens.typography.lineHeight.base,
    },
  },
  errorLabel: {
    '& .MuiCheckbox-root': {
      color: idsTokens.checkbox.error.color.control.border.enabled,
      '&.Mui-checked svg, &.MuiCheckbox-indeterminate svg': {
        fill: idsTokens.checkbox.error.color.control.background.selected.enabled,
        '& rect:last-of-type, & path:last-of-type': {
          fill: idsTokens.checkbox.error.color.control.icon.enabled,
        },
      },
    },
    '&:hover:not(:active)': {
      '& .MuiCheckbox-root:not(.MuiCheckbox-indeterminate)': {
        '& svg': {
          fill: idsTokens.checkbox.error.color.control.border.hover,
          color: 'unset',
          '& rect': {
            fill: idsTokens.checkbox.error.color.control.background.unselected.hover,
          },
        },
      },
      // Default hover checked & indeterminate
      '& .Mui-checked svg, & .MuiCheckbox-indeterminate svg': {
        fill: idsTokens.checkbox.default.color.control.background.selected.hover,
        '& rect:last-of-type, & path:last-of-type': {
          fill: idsTokens.checkbox.default.color.control.icon.hover,
        },
      },
    },
    '&:active:hover': {
      '& .MuiCheckbox-root:not(.MuiCheckbox-indeterminate)': {
        '& svg': {
          fill: idsTokens.checkbox.error.color.control.border.pressed,
          color: 'unset',
          '& rect': {
            fill: `${idsTokens.checkbox.error.color.control.background.unselected.pressed} !important`,
          },
        },
      },
    },
  },
  disabledChecked: {
    '&.Mui-disabled .MuiFormControlLabel-label': {
      color: `${idsTokens.checkbox.default.color.label.disabled} !important`,
    },
  },
  errorChecked: {
    '& .Mui-checked': {
      color: 'unset',
      '& svg': {
        fill: idsTokens.checkbox.error.color.control.background.selected.enabled,
        '& path:last-of-type': {
          fill: idsTokens.checkbox.error.color.control.icon.enabled,
        },
      },
    },
    '& .MuiCheckbox-indeterminate': {
      '& svg': {
        fill: idsTokens.checkbox.error.color.control.background.selected.enabled,
        '& rect:last-of-type': {
          fill: idsTokens.checkbox.error.color.control.icon.enabled,
        },
      },
    },
    '&:not(.Mui-disabled):hover:not(:active)': {
      '& .Mui-checked svg, & .MuiCheckbox-indeterminate svg': {
        fill: idsTokens.checkbox.error.color.control.background.selected.hover,
        '& rect:last-of-type, &path:last-of-type': {
          fill: idsTokens.checkbox.error.color.control.icon.hover,
        },
      },
    },
    '&:not(.Mui-disabled):active:hover': {
      '& .Mui-checked svg, & .MuiCheckbox-indeterminate svg': {
        fill: idsTokens.checkbox.error.color.control.background.selected.pressed,
        '& rect:last-of-type, &path:last-of-type': {
          fill: idsTokens.checkbox.error.color.control.icon.pressed,
        },
      },
    },
  },
  smallRoot: {
    gap: idsTokens.checkbox.default.spacing.small.gap,
    paddingRight: idsTokens.checkbox.default.spacing.small.right,
    paddingBottom: idsTokens.checkbox.default.spacing.small.bottom,
    paddingTop: idsTokens.checkbox.default.spacing.small.top,
    paddingLeft: idsTokens.checkbox.default.spacing.small.left,
    '& MuiCheckbox-root': {
      top: 0,
      paddingBottom: 4,
    },
    '& .MuiTypography-root.MuiFormControlLabel-label': {
      paddingTop: 2,
      paddingBottom: 0,
      lineHeight: idsTokens.typography.lineHeight.sm,
      fontSize: idsTokens.typography.font.size.sm,
      fontWeight: idsTokens.typography.font.weight.regular,
      letterSpacing: idsTokens.typography.spacing.letter.md,
    },
  },
  noLabel: {
    '&:has(.Mui-focusVisible)': {
      outlineOffset: 'unset',
      outline: 'unset',
    },
    '& .MuiCheckbox-root': {
      paddingTop: 0,
      paddingBottom: 0,
      '&.Mui-focusVisible': {
        outlineOffset: -2,
        outlineStyle: 'auto',
        outline: `${idsTokens.functional.borderWidth.focus.default} solid ${idsTokens.functional.color.border.focus}`,
        borderRadius: idsTokens.checkbox.default.radius.focus,
      },
    },
  },
  singleCheckbox: {
    padding: '8px 8px 8px 2px',
  },
  smallSingleCheckbox: {
    padding: '4px 4px 4px 2px',
  },
});

const useStyles = makeStyles()(styles);

export const Checkbox = ({
  checked,
  indeterminate,
  disabled,
  label,
  required,
  asteriskLeft,
  value,
  error,
  size = 'medium',
  style,
  className,
  inGroup,
  tabIndex,
  forwardedRef: ref,
  ...rest
}) => {
  const { classes, cx } = useStyles();

  return (
    <FormControlLabel
      checked={checked}
      disabled={disabled}
      className={cx(
        classes.root,
        (!label || label === ' ') && classes.noLabel,
        size === 'small' && classes.smallRoot,
        error && !indeterminate && !checked && !disabled && classes.errorLabel,
        error && (indeterminate || checked) && classes.errorChecked,
        disabled && (checked || indeterminate) && classes.disabledChecked,
        !inGroup && (size === 'small' ? classes.smallSingleCheckbox : classes.singleCheckbox),
        className
      )}
      label={
        label || (required && asteriskLeft && <span className={classes.asterisk}>{'\u00A0*'}</span>)
      }
      control={
        <MuiCheckbox
          color="primary"
          checked={checked}
          disabled={disabled}
          indeterminate={indeterminate}
          disableRipple
          checkedIcon={<CheckboxCheckedIcon />}
          icon={error && !disabled ? <CheckboxErrorIcon /> : <CheckboxUncheckedIcon />}
          indeterminateIcon={<CheckboxIndeterminateIcon />}
          value={value}
          tabIndex={tabIndex}
          className={cx(size === 'small' && classes.small)}
        />
      }
      size={size}
      style={{ ...(!label && { minHeight: size === 'small' ? 24 : 32 }), ...style }}
      {...rest}
      ref={ref}
    />
  );
};

Checkbox.propTypes = {
  /** If `true`, the component is checked. */
  checked: PropTypes.bool,
  /** If `true`, the component is disabled. */
  disabled: PropTypes.bool,
  /** If `true`, the component is displayed in an error state. */
  error: PropTypes.bool,
  /** If `true`, the component appears indeterminate. */
  indeterminate: PropTypes.bool,
  /** The label content. */
  label: PropTypes.node,
  /**
   * Callback fired when the component is selected or deselected.
   *
   * @param {React.SyntheticEvent} event The event source of the callback.
   * You can pull out the new checked state by accessing event.target.checked (boolean).
   */
  onChange: PropTypes.func,
  /** The size of the component. */
  size: PropTypes.oneOf(['small', 'medium']),
  /** Tab index of the checkbox input. Use -1 to remove from tab order (e.g. when focus is managed by parent). */
  tabIndex: PropTypes.number,
  /** The value of component. */
  value: PropTypes.any,
};

export default withRef()(Checkbox);
